import { PhotoMetadataRecord, PhotoRecord } from "@/lib/catalog/types"
import { getFileForPhoto } from "../files-state"
import {
	commitTrackingPerformanceValue,
	setPerformanceStat,
	startTrackingPerformanceValue,
} from "@/utils/performance-stats"
import { CatalogManager } from "@/lib/catalog"
import { createBasicPhotoMetadata } from "@/lib/catalog/utils"
import { MetadataLoader } from "@/lib/metadata/metadata-loader"
import { PreviewSize } from "@/store/previews/previews-types"
import { FilesInfo } from "@/storage/file-system"
import { analytics } from "@/telemetry/amplitude"
import { Sentry } from "@/telemetry/sentry"
import { getFilename } from "@/utils/file"
import PQueue from "p-queue"
import {
	loadingStateAtom,
	metadataFailureCountAtom,
	metadataIndexingProgressAtom,
} from "../app-state"
import {
	metadataAtom,
	sortedPhotosAtom,
	updatePhotos,
	updatePhotosMetadata,
	getPhotoMetadata,
} from "../photo/photos-state"
import { ProjectItem } from "../project/projects-service"
import { projectAtom } from "../project/projects-state"
import { store } from "../store"
import { PhotoId } from "../undoable-state"
import { previewImporter } from "./preview-importing"
import { cullingImporter } from "./culling-importing"
import {
	getCurrentProjectMetadataAnalyticsInfo,
	getFirstFileMetadataAnalyticsInfo,
} from "../project/project-metadata"
import {
	beginTaskPerformanceMeasurement,
	beginTaskSingleItemPerformanceMeasurement,
	commitTaskPerformanceMeasurement,
	commitTaskSingleItemPerformanceMeasurement,
} from "../debug/task-performance-state"

class MetadataImporter {
	public isInProgress = false

	// Internal queue to manage metadata collection concurrency.
	private metadataConcurrentQueue = new PQueue({ autoStart: false })

	// Metadata results collected until the phase is complete.
	private collectedDataBuffer = new Map<
		PhotoId,
		{ metadata: PhotoMetadataRecord; photo: Partial<PhotoRecord> }
	>()

	// External data
	public startProjectId: string = ""
	public filesInfo?: FilesInfo

	private get maxThreadsCount() {
		return Math.max(2, navigator.hardwareConcurrency - 3)
	}

	private runId = 0
	private currentImport?: Promise<boolean>

	constructor() {
		this.metadataConcurrentQueue.on("error", (e) => Sentry.captureException(e))
	}

	pause() {
		this.metadataConcurrentQueue.pause()
	}

	async pauseAndWaitForInFlightTasksToComplete() {
		this.metadataConcurrentQueue.pause()
		await this.metadataConcurrentQueue.onPendingZero()
	}

	resumeIfNeeded() {
		if (this.metadataConcurrentQueue.size == 0) {
			return
		}

		this.metadataConcurrentQueue.start()
	}

	start(project: ProjectItem, filesInfo?: FilesInfo, forceRestart = false): Promise<boolean> {
		// Setup joins the prefetch, including a completed run, without discarding
		// buffered results or retrying failed files from the same import.
		if (!forceRestart && this.startProjectId === project.id && this.currentImport) {
			this.resumeIfNeeded()
			return this.currentImport
		}
		const previousRunStopped = this.pauseAndCancel()
		const runId = this.runId
		this.startProjectId = project.id
		this.filesInfo = filesInfo
		this.isInProgress = true
		const allPhotos = store.get(sortedPhotosAtom)
		const metadata = store.get(metadataAtom)
		store.set(metadataIndexingProgressAtom, {
			projectId: project.id,
			completed: allPhotos.filter((photo) => metadata[photo.id]?.isComplete).length,
			total: allPhotos.length,
		})
		this.currentImport = this.importMetadata(project, runId, previousRunStopped)
		return this.currentImport
	}

	private isCurrentRun(runId: number) {
		return this.runId === runId && store.get(projectAtom)?.id === this.startProjectId
	}

	private async importMetadata(
		project: ProjectItem,
		runId: number,
		previousRunStopped: Promise<void>,
	): Promise<boolean> {
		try {
			await previousRunStopped
			if (!this.isCurrentRun(runId)) return false
			const filesInfo = this.filesInfo
			const allPhotos = store.get(sortedPhotosAtom)
			void MetadataLoader.setOptions({ jobs: this.maxThreadsCount })
			this.metadataConcurrentQueue.concurrency = this.maxThreadsCount
			const allMetadata = await CatalogManager.fetchAllPhotoMetadata()
			if (!this.isCurrentRun(runId)) return false
			store.set(metadataAtom, allMetadata)

			/* Metadata as fast as we can */

			const missingMetadataPhotos = allPhotos.filter((photo) => {
				const metadata = allMetadata[photo.id]
				return !metadata || !metadata.isComplete
			})
			store.set(metadataIndexingProgressAtom, {
				projectId: project.id,
				completed: allPhotos.length - missingMetadataPhotos.length,
				total: allPhotos.length,
			})

			if (missingMetadataPhotos.length > 0) {
				store.set(loadingStateAtom, { status: "importing" })

				const analyticsInfo = {
					projectId: project.id,
					styleId: project.styleId,
					totalCount: filesInfo?.files.length ?? allPhotos.length,
					sideCarsCount: filesInfo ? Object.keys(filesInfo?.sideCars).length : "Unknown",
				}
				analytics.trackEvent({
					label: "import_metadata_start",
					type: "event",
					customProperties: analyticsInfo,
				})
			}

			// Reset AI queues before starting the next processing phase.
			await cullingImporter.clearTasks()
			if (!this.isCurrentRun(runId)) return false

			// Preserve blocking OOB calls because they won't be rescheduled.
			await previewImporter.clearTasks({ preserve: ["slow-preview-foreground"] })
			if (!this.isCurrentRun(runId)) return false
			previewImporter.resumeIfNeeded()

			beginTaskPerformanceMeasurement("import-metadata")

			for (const photo of missingMetadataPhotos) {
				this.metadataConcurrentQueue
					.add(async (): Promise<void> => {
						try {
							await this.extractPhotoMetadata(
								photo,
								runId,
								filesInfo?.sideCars[getFilename(photo.fileName)],
							)
						} catch (error) {
							Sentry.captureException(error)
							if (this.isCurrentRun(runId)) {
								store.set(metadataFailureCountAtom, (count) => count + 1)
							}
						} finally {
							// Count the file once, after retries, even if extraction failed.
							if (this.isCurrentRun(runId)) {
								store.set(metadataIndexingProgressAtom, (progress) =>
									progress
										? { ...progress, completed: progress.completed + 1 }
										: null,
								)
							}
						}
					})
					.catch((e) => {
						Sentry.captureException(e)
					})
			}

			this.metadataConcurrentQueue.start()

			await this.metadataConcurrentQueue.onIdle()
			if (!this.isCurrentRun(runId)) return false
			this.flushCollectedDataBuffer()
			if (store.get(loadingStateAtom).status === "importing") {
				store.set(loadingStateAtom, { status: null })
				void MetadataLoader.setOptions({ jobs: 2 })
				analytics.trackEvent({
					label: "import_metadata_finish",
					type: "event",
					customProperties: {
						...getCurrentProjectMetadataAnalyticsInfo(),
						...getFirstFileMetadataAnalyticsInfo(),
					},
				})
			}
			commitTaskPerformanceMeasurement("import-metadata")
			return true
		} catch (error) {
			if (!this.isCurrentRun(runId)) return false
			this.currentImport = undefined
			throw error
		} finally {
			if (this.isCurrentRun(runId)) this.isInProgress = false
		}
	}

	cancel() {
		this.resetRun()
		// Clear RawMetadataLoader queue to cancel in-flight worker tasks.
		void MetadataLoader.clearQueue()
	}

	private resetRun() {
		// Invalidate before draining so a canceled run cannot publish results or
		// complete setup, even when the next import is for the same project.
		this.runId++
		this.currentImport = undefined
		this.isInProgress = false
		this.metadataConcurrentQueue.clear()
		this.metadataConcurrentQueue.pause()
		this.collectedDataBuffer.clear()
		store.set(metadataFailureCountAtom, 0)
		store.set(metadataIndexingProgressAtom, null)
		if (store.get(loadingStateAtom).status === "importing") {
			store.set(loadingStateAtom, { status: null })
		}
		this.startProjectId = ""
		this.filesInfo = undefined
	}

	/**
	 * Async cancel that waits for in-flight tasks to complete before cleanup.
	 * Use this when you need to ensure no tasks are running after the call returns.
	 */
	async pauseAndCancel() {
		this.resetRun()
		await this.metadataConcurrentQueue.onPendingZero()
		await MetadataLoader.clearQueue()
	}

	private flushCollectedDataBuffer() {
		if (this.collectedDataBuffer.size === 0) {
			return
		}

		// Atomically swap buffer to prevent race condition:
		// Capture current buffer and replace with empty one before processing
		const bufferToFlush = this.collectedDataBuffer
		this.collectedDataBuffer = new Map<
			PhotoId,
			{ metadata: PhotoMetadataRecord; photo: Partial<PhotoRecord> }
		>()

		const project = store.get(projectAtom)

		if (!project) {
			return
		}

		const handleUpdateError = (error: unknown, source: string) => {
			console.error(`Error updating ${source}`, error)
			Sentry.captureException(error, { tags: { source } })
		}

		updatePhotos((photos) => {
			bufferToFlush.forEach((value, id) => {
				photos[id] = value.photo
			})
		}).catch((error) => handleUpdateError(error, "updatePhotos"))

		updatePhotosMetadata((metadatas) => {
			bufferToFlush.forEach((value, id) => {
				metadatas[id] = value.metadata
			})
		}, true).catch((error) => handleUpdateError(error, "updatePhotosMetadata"))
	}

	private async extractPhotoMetadata(
		photo: PhotoRecord,
		runId: number,
		sidecar?: File,
		retryCount: number = 0,
	): Promise<void> {
		if (!this.isCurrentRun(runId)) return
		const file = getFileForPhoto(photo)

		if (!file) {
			store.set(metadataFailureCountAtom, (count) => count + 1)
			return
		}

		let metadata: PhotoMetadataRecord | undefined = getPhotoMetadata(photo.id)

		if (metadata && metadata.isComplete) {
			return
		}

		// Metadata only: previews belong to the subsequent culling phase.
		try {
			const measurementTask = beginTaskSingleItemPerformanceMeasurement("import-metadata")

			// First touchpoint that has the live `File` for this photo —
			// stamp filename + size on the per-photo descriptor here so
			// downstream consumers (perf report, devtools) don't have to
			// re-derive them from the photo record.
			setPerformanceStat(photo.id, "name", file.name)
			setPerformanceStat(photo.id, "fileSize", file.size)
			startTrackingPerformanceValue(photo.id, "metadataRead")

			const result = await MetadataLoader.getMetadata(file, {
				sidecar,
				previewSize: 0 as PreviewSize,
			})

			metadata = result.record

			if (!this.isCurrentRun(runId)) {
				//project closed or switched project, no need to store any more
				return
			}

			if (!metadata) {
				metadata = createBasicPhotoMetadata(file, photo.id)
			}

			metadata.photoId = photo.id

			this.collectedDataBuffer.set(photo.id, {
				metadata,
				photo: {
					rating: photo.rating ?? metadata.rating,
					colorTag: photo.colorTag ?? metadata.colorTag,
					flag: photo.flag ?? metadata.flag,
				},
			})

			if (!metadata.isComplete) {
				this.flushCollectedDataBuffer()
				store.set(metadataFailureCountAtom, (count) => count + 1)
			}

			commitTaskSingleItemPerformanceMeasurement(measurementTask)
			commitTrackingPerformanceValue(photo.id, "metadataRead")
		} catch (error) {
			if (!this.isCurrentRun(runId)) return
			if (error instanceof Error && error.name === "TimeoutError") {
				console.error(
					`[extractPhotoMetadata] ⏱️ Timeout extracting metadata for ${file.name}`,
				)
				// Retry up to 2 times on timeout
				if (retryCount < 2) {
					console.log(
						`[extractPhotoMetadata] 🔄 Retrying metadata extraction for ${
							file.name
						} (attempt ${retryCount + 2}/3)`,
					)
					return await this.extractPhotoMetadata(photo, runId, sidecar, retryCount + 1)
				}
				console.error(
					`[extractPhotoMetadata] ❌ Max retries reached for ${file.name}, giving up`,
				)
			} else {
				console.error(
					`[extractPhotoMetadata] Error importing metadata for ${file.name}`,
					error,
				)
			}
			Sentry.captureException(error)
			const fallbackMetadata = createBasicPhotoMetadata(file, photo.id)
			this.collectedDataBuffer.set(photo.id, {
				metadata: fallbackMetadata,
				photo: {
					rating: photo.rating ?? fallbackMetadata.rating,
					colorTag: photo.colorTag ?? fallbackMetadata.colorTag,
					flag: photo.flag ?? fallbackMetadata.flag,
				},
			})
			this.flushCollectedDataBuffer()
			store.set(metadataFailureCountAtom, (count) => count + 1)
		}
	}
}

export const metadataImporter = new MetadataImporter()
